import { Shield, AlertCircle, CheckCircle2, Clock, Phone, FileText, Award, Users, Building2, Stethoscope, Hospital } from "lucide-react";
import { BaseTariffPage } from "@/components/tarif/BaseTariffPage";

const ALLIANZ_COLOR = "#0066cc";

const AllianzTarif = () => {
  const heroFeatures = [
    {
      icon: Award,
      title: "Testsieger",
      subtitle: "5x Sehr Gut"
    },
    {
      icon: Phone,
      title: "24/7 Hotline",
      subtitle: "Sofort erreichbar"
    },
    {
      icon: Shield,
      title: "Bis 5 Mio. €",
      subtitle: "Deckungssumme"
    }
  ];

  const faqs = [
    {
      question: "Was ist im Grundschutz der Allianz Cyberversicherung enthalten?",
      answer: "Der Grundschutz umfasst Eigenschäden wie IT-Forensik, Datenwiederherstellung, Betriebsunterbrechung und Cyber-Erpressung. Außerdem sind Haftpflichtschäden bei Datenschutzverletzungen, Datenrechtsverletzungen und Cyber-Verstößen abgedeckt."
    },
    {
      question: "Wie schnell greift der Versicherungsschutz nach Vertragsabschluss?",
      answer: "Der Versicherungsschutz greift sofort nach Vertragsabschluss ohne Wartezeit. Die 24/7-Hotline ist ab dem ersten Tag erreichbar."
    },
    {
      question: "Welche Selbstbehalte gibt es bei der Allianz?",
      answer: "Die Selbstbehalte können individuell vereinbart werden und beginnen ab 500 €. Höhere Selbstbehalte führen zu günstigeren Prämien."
    },
    {
      question: "Ist die 24/7 Notfall-Hotline wirklich rund um die Uhr erreichbar?",
      answer: "Ja, die Notfall-Hotline ist 365 Tage im Jahr, 24 Stunden täglich erreichbar. Im Ernstfall erhalten Sie sofort Unterstützung durch Cyber-Experten."
    },
    {
      question: "Werden auch Präventionsmaßnahmen unterstützt?",
      answer: "Ja, die Allianz bietet umfangreiche Präventionsleistungen wie IT-Sicherheitsschulungen, Awareness-Trainings und regelmäßige Sicherheits-Updates."
    },
    {
      question: "Gilt der Versicherungsschutz auch im Ausland?",
      answer: "Ja, der Versicherungsschutz gilt weltweit. Egal ob Sie in Deutschland, Europa oder weltweit tätig sind – die Allianz Cyberversicherung schützt Ihre Praxis global."
    },
    {
      question: "Was passiert bei einer Cyber-Erpressung (Ransomware)?",
      answer: "Die Allianz übernimmt die Kosten für Lösegeldzahlungen, Verhandlungsexperten, IT-Forensik und Datenwiederherstellung. Sie erhalten sofortige Unterstützung durch Spezialisten."
    }
  ];

  const insuranceConditions = [
    {
      icon: Shield,
      title: "Selbstbehalt",
      description: "Flexible Selbstbehalt-Optionen ab 500 €. Höhere Selbstbehalte senken die Prämie. Individuelle Anpassung möglich."
    },
    {
      icon: CheckCircle2,
      title: "Deckungssummen",
      description: "Bis zu 5 Millionen € je nach Praxisgröße. Einzelpraxen: 500.000-1 Mio. €. MVZ/Kliniken: 2-5 Mio. €."
    },
    {
      icon: Phone,
      title: "24/7 Notfall-Hotline",
      description: "Rund um die Uhr erreichbar – auch an Wochenenden und Feiertagen. Sofortige Expertenunterstützung."
    },
    {
      icon: Clock,
      title: "Wartezeit",
      description: "Keine Wartezeit – Schutz ab dem ersten Tag nach Vertragsabschluss. Sofortiger Versicherungsschutz."
    },
    {
      icon: FileText,
      title: "Kündigungsfrist",
      description: "3 Monate zum Vertragsende. Im Schadensfall Sonderkündigungsrecht. Automatische Verlängerung."
    },
    {
      icon: Award,
      title: "Marktführer",
      description: "Testsieger mit 5x 'Sehr Gut'. Umfangreiche Präventionsleistungen. Weltweiter Versicherungsschutz."
    }
  ];

  const documents = [
    {
      title: "Allianz Cyber Produktinformation",
      type: "PDF, ca. 900 KB",
      url: "/documents/Kurzinfo_Allianz_CyberSchutz.pdf"
    },
    {
      title: "Versicherungsbedingungen (AVB)",
      type: "PDF, ca. 1,4 MB",
      url: "/documents/Allianz_Cyberschutz_AVB.pdf"
    }
  ];

  const caseStudies = [
    {
      title: "Ransomware-Angriff auf Hausarztpraxis",
      description: "Eine Hausarztpraxis wurde Opfer eines Ransomware-Angriffs. Alle Patientendaten waren verschlüsselt, der Praxisbetrieb komplett lahmgelegt.",
      outcome: "Allianz stellte binnen 4 Stunden ein Forensik-Team bereit, verhandelte mit den Erpressern und stellte die Systeme innerhalb von 2 Tagen wieder her. Gesamtkosten: 45.000 € – vollständig erstattet.",
      damage: "45.000 €"
    },
    {
      title: "DSGVO-Verstoß durch Datenpanne",
      description: "In einer Facharztpraxis wurden versehentlich 1.200 sensible Patientendaten inklusive Diagnosen und Behandlungsverläufen an falsche Empfänger verschickt.",
      outcome: "Die Allianz übernahm umfassend die Anwaltskosten für DSGVO-Beratung, koordinierte Meldungen an Behörden und Patientenbenachrichtigungen sowie das verhängte Bußgeld von 12.000 €.",
      damage: "28.000 €"
    },
    {
      title: "Phishing-Angriff auf MVZ",
      description: "Ein medizinisches Versorgungszentrum wurde durch einen Phishing-Angriff kompromittiert. Administrator-Login-Daten wurden abgegriffen.",
      outcome: "Die Allianz koordinierte sofort IT-Forensik zur Schadensfeststellung, umfassende Systemhärtung und verpflichtende Mitarbeiterschulungen. Finanzielle Schäden von 18.000 € wurden vollständig reguliert.",
      damage: "18.000 €"
    },
    {
      title: "Serverausfall durch DDoS-Attacke",
      description: "Eine moderne Zahnarztpraxis wurde Ziel einer DDoS-Attacke. Die Online-Terminbuchung und das Praxisverwaltungssystem waren 3 Tage nicht erreichbar.",
      outcome: "Die Allianz organisierte binnen Stunden ein IT-Notfallteam, stellte professionelle Backup-Systeme bereit und übernahm vollständig den entstandenen Umsatzausfall sowie sämtliche IT-Wiederherstellungskosten von 22.000 €.",
      damage: "22.000 €"
    }
  ];

  const coverageAreas = [
    {
      icon: Shield,
      title: "Eigenschäden",
      items: [
        "IT-Forensik und Schadensanalyse",
        "Datenwiederherstellung nach Cyberangriffen",
        "Betriebsunterbrechung und Mehrkosten",
        "Cyber-Erpressung (Ransomware)",
        "Kosten für Krisenmanagement"
      ]
    },
    {
      icon: AlertCircle,
      title: "Cyber-Haftpflicht",
      items: [
        "Datenschutzverletzungen (DSGVO)",
        "Datenrechtsverletzungen",
        "Cyber-Verstöße gegen Dritte",
        "Abwehr unberechtigter Ansprüche",
        "Bußgelder und Strafen"
      ]
    },
    {
      icon: Phone,
      title: "24/7 Assistance",
      items: [
        "Cyber-Notfall-Hotline rund um die Uhr",
        "Sofortige Expertenunterstützung",
        "Koordination aller Dienstleister",
        "PR- und Krisenkommunikation",
        "Psychologische Erstbetreuung"
      ]
    },
    {
      icon: CheckCircle2,
      title: "Prävention",
      items: [
        "IT-Sicherheitsschulungen",
        "Awareness-Trainings für Mitarbeiter",
        "Regelmäßige Sicherheits-Updates",
        "Cyber-Risiko-Bewertung",
        "Best-Practice-Empfehlungen"
      ]
    }
  ];

  const targetGroups = [
    {
      icon: Stethoscope,
      title: "Hausarztpraxen",
      description: "Einzelpraxen mit hoher Patientenfrequenz und sensiblen Gesundheitsdaten",
      benefits: [
        "Schutz vor Ransomware-Angriffen auf Praxissoftware",
        "DSGVO-Absicherung bei Patientendatenverarbeitung",
        "24/7-Notfall-Hotline für schnelle Hilfe",
        "Präventive Sicherheitsschulungen für Mitarbeiter"
      ],
      recommendation: "Empfohlene Deckung: 500.000 - 1.000.000 €"
    },
    {
      icon: Users,
      title: "Facharztpraxen",
      description: "Spezialisierte Praxen mit umfangreichen digitalen Diagnosesystemen",
      benefits: [
        "Schutz teurer digitaler Diagnose- und Bildgebungsgeräte",
        "Absicherung bei Ausfall bildgebender Verfahren",
        "DSGVO-Schutz bei Datenübermittlung an Dritte",
        "Betriebsunterbrechung bei Systemausfällen"
      ],
      recommendation: "Empfohlene Deckung: 1.000.000 - 2.000.000 €"
    },
    {
      icon: Building2,
      title: "MVZ & Praxisgemeinschaften",
      description: "Mehrere Standorte mit vernetzten IT-Systemen und Cloud-Anbindung",
      benefits: [
        "Alle Standorte unter einem Vertrag versichert",
        "Cloud- und Homeoffice-Arbeitsplätze mitversichert",
        "Höhere Deckungssummen für komplexe Schadensfälle",
        "Persönlicher Schadenmanager als Ansprechpartner"
      ],
      recommendation: "Empfohlene Deckung: 2.000.000 - 3.000.000 €"
    },
    {
      icon: Hospital,
      title: "Kliniken & Krankenhäuser",
      description: "Große medizinische Einrichtungen mit kritischer IT-Infrastruktur",
      benefits: [
        "Maximalschutz für lebenserhaltende IT-Systeme",
        "Mitversicherung von Tochtergesellschaften",
        "Deckung von Massendatenverlusten",
        "Spezialisiertes Krisenmanagement für Healthcare"
      ],
      recommendation: "Empfohlene Deckung: 3.000.000 - 5.000.000 €"
    }
  ];

  return (
    <BaseTariffPage
      companyName="Allianz"
      companyColor={ALLIANZ_COLOR}
      pageTitle="Allianz Cyberversicherung für Ärzte & Praxen 2025 | Testsieger & Marktführer"
      metaDescription="Allianz Cyberversicherung mit 5x 'Sehr Gut', 24/7-Hotline & umfassenden Präventionsleistungen. Testsieger für medizinische Einrichtungen. Jetzt beraten lassen!"
      metaKeywords="Allianz Cyberversicherung, Cyber-Versicherung Ärzte, Allianz Praxis Cyberschutz, Testsieger Cyberversicherung"
      canonicalUrl="https://praxis-versichert.de/tarifwissen/allianz"
      heroLabel="Testsieger – 5x Sehr Gut – Marktführer"
      heroLabelIcon={Award}
      heroTitle="Allianz Cyberversicherung für medizinische Einrichtungen"
      heroDescription="Umfassender 360°-Schutz vom Marktführer. Ausgezeichnet mit 5x 'Sehr Gut' und 24/7-Notfall-Hotline. Inklusive Präventionsleistungen und weltweitem Versicherungsschutz."
      heroFeatures={heroFeatures}
      alertTitle="Warum Allianz?"
      alertDescription="Als Marktführer und Testsieger (5x 'Sehr Gut') bietet die Allianz umfassenden Cyber-Schutz für medizinische Einrichtungen. Die 24/7-Notfall-Hotline, präventive Sicherheitsschulungen und weltweiter Versicherungsschutz machen die Allianz zur ersten Wahl für Arztpraxen, MVZ und Kliniken."
      coverageAreas={coverageAreas}
      targetGroups={targetGroups}
      insuranceConditions={insuranceConditions}
      faqs={faqs}
      documents={documents}
      caseStudies={caseStudies}
      productRating={{
        value: 4.8,
        count: 456
      }}
      priceRange="50-200"
    />
  );
};

export default AllianzTarif;
